import * as ActionTypes from "./actionTypes";
import * as Actions from "./action";
import { Quotation, QuotationTemplate } from "src/common/models/quotation";
import { ErrorAction } from "src/common/apiLoader/state/actions";
import { QuotationError, QuotationErrorOrigin } from "./types";

export interface QuotationState {
    currentQuotation?: Partial<Quotation>
    quotations?: Quotation[]
    quotationTemplates?: QuotationTemplate[]
    getQuotationFileSuccess: boolean
    upsertQuotationSuccess: boolean
    upsertQuotationTemplateSuccess: boolean
    generateQuotationPdfSuccess: boolean
    sendQuotationSuccess: boolean
    updateQuotationStatusSuccess: boolean
    quotationFile?: string
    error?: QuotationError
}

const initialState: QuotationState = {
    getQuotationFileSuccess: false,
    upsertQuotationSuccess: false,
    upsertQuotationTemplateSuccess: false,
    generateQuotationPdfSuccess: false,
    sendQuotationSuccess: false,
    updateQuotationStatusSuccess: false,
}

const quotationReducer = (state: QuotationState = initialState, action: any): QuotationState => {
    switch (action.type) {
        case ActionTypes.UPDATE_CURRENT_QUOTATION: {
            const updateAction = action as Actions.UpdateQuotationAction;

            const mergedDetail = updateAction.quotationFields?.detail || state.currentQuotation?.detail || [];

            const total = mergedDetail?.reduce((total, item) => {
                return total + item.total
            }, 0)

            return {
                ...state,
                currentQuotation: {
                    ...state.currentQuotation,
                    ...updateAction.quotationFields,
                    total,
                },
            }
        }
        case ActionTypes.GET_QUOTATION_FILE_API: {
            return {
                ...state,
                getQuotationFileSuccess: false,
            }
        }
        case ActionTypes.UPSERT_QUOTATION_API: {
            return {
                ...state,
                upsertQuotationSuccess: false,
            }
        }
        case ActionTypes.UPSERT_QUOTATION_API_SUCCESS: {
            const successAction = action as Actions.UpsertQuotationSuccessAction

            return {
                ...state,
                upsertQuotationSuccess: true,
                currentQuotation: successAction.quotation,
            }
        }
        case ActionTypes.UPSERT_QUOTATION_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                upsertQuotationSuccess: false,
                error: {
                    type: QuotationErrorOrigin.CREATE_QUOTATION,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.GET_QUOTATIONS_API_SUCCESS: {
            const successAction = action as Actions.GetQuotationsSuccessAction

            return {
                ...state,
                quotations: successAction.quotations,
            }
        }
        case ActionTypes.GET_QUOTATIONS_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.GET_QUOTATIONS,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.GET_QUOTATION_BY_ID_API_SUCCESS: {
            const successAction = action as Actions.GetQuotationByIdSuccessAction

            return {
                ...state,
                currentQuotation: successAction.quotation,
            }
        }
        case ActionTypes.GET_QUOTATION_BY_ID_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.GET_QUOTATION_BY_ID,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.GET_QUOTATION_TEMPLATES_API_SUCCESS: {
            const successAction = action as Actions.GetQuotationTemplatesSuccessAction

            return {
                ...state,
                quotationTemplates: successAction.templates,
            }
        }
        case ActionTypes.UPSERT_QUOTATION_TEMPLATE_API: {
            return {
                ...state,
                upsertQuotationTemplateSuccess: false,
            }
        }
        case ActionTypes.UPSERT_QUOTATION_TEMPLATE_API_SUCCESS: {
            const successAction = action as Actions.UpsertQuotationTemplateSuccessAction
            const templates = state.quotationTemplates || []
            const existingIndex = templates.findIndex(template => template.id === successAction.template.id)
            const quotationTemplates = existingIndex >= 0
                ? templates.map(template => template.id === successAction.template.id ? successAction.template : template)
                : [...templates, successAction.template]

            return {
                ...state,
                upsertQuotationTemplateSuccess: true,
                quotationTemplates,
            }
        }
        case ActionTypes.UPSERT_QUOTATION_TEMPLATE_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                upsertQuotationTemplateSuccess: false,
                error: {
                    type: QuotationErrorOrigin.GET_QUOTATION_TEMPLATES,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.GET_QUOTATION_TEMPLATES_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.GET_QUOTATION_TEMPLATES,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.GENERATE_QUOTATION_PDF_API: {
            return {
                ...state,
                generateQuotationPdfSuccess: false,
                quotationFile: undefined,
            }
        }
        case ActionTypes.GENERATE_QUOTATION_PDF_API_SUCCESS: {
            const successAction = action as Actions.GenerateQuotationPdfSuccessAction

            return {
                ...state,
                generateQuotationPdfSuccess: true,
                quotationFile: successAction.result,
            }
        }
        case ActionTypes.GENERATE_QUOTATION_PDF_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.GENERATE_QUOTATION_PDF,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.SEND_QUOTATION_API: {
            return {
                ...state,
                sendQuotationSuccess: false,
            }
        }
        case ActionTypes.SEND_QUOTATION_API_SUCCESS: {
            return {
                ...state,
                sendQuotationSuccess: true,
            }
        }
        case ActionTypes.SEND_QUOTATION_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.SEND_QUOTATION,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.UPDATE_QUOTATION_STATUS_API: {
            return {
                ...state,
                updateQuotationStatusSuccess: false,
            }
        }
        case ActionTypes.UPDATE_QUOTATION_STATUS_API_SUCCESS: {
            return {
                ...state,
                updateQuotationStatusSuccess: true,
            }
        }
        case ActionTypes.UPDATE_QUOTATION_STATUS_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.UPDATE_QUOTATION_STATUS,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.GET_QUOTATION_FILE_API_SUCCESS: {
            const successAction = action as Actions.GetQuotationFileSuccessAction

            return {
                ...state,
                getQuotationFileSuccess: true,
                quotationFile: successAction.result
            }
        }
        case ActionTypes.GET_QUOTATION_FILE_API_ERROR: {
            const errorAction = action as ErrorAction;

            return {
                ...state,
                error: {
                    type: QuotationErrorOrigin.GET_QUOTATION_FILE,
                    detail: errorAction.error
                }
            }
        }
        case ActionTypes.CLEAN_QUOTATION_FILE: {
            return {
                ...state,
                quotationFile: undefined,
                generateQuotationPdfSuccess: false,
            }
        }
        case ActionTypes.CLEAN_CURRENT_QUOTATION: {
            return {
                ...state,
                currentQuotation: undefined,
                upsertQuotationSuccess: false,
                generateQuotationPdfSuccess: false,
                sendQuotationSuccess: false,
                updateQuotationStatusSuccess: false,
                quotationFile: undefined,
            }
        }
    }
    return state;
};


export default quotationReducer;
